/**
 * MigratedResourcesView: Settings -> Resource Types for a MIGRATED dealer
 * (IDI-408).
 *
 * Legacy BIDIT kept two levels: the dealer's own Resource TYPES ("Ladder")
 * and the physical RESOURCES under each ("8' Ladder", "20' Ladder"). The
 * built-in Equipment / Vehicles / Tools grouping does not exist in legacy, so
 * for a dealer with migrated resources this view shows the dealer's own types
 * as groups with their resources listed under them.
 *
 * Read-only for now: editing migrated types/resources is a follow-up. The
 * WO / job resource pickers still read `resource_types`, unchanged.
 */
import React, { useMemo, useState } from 'react';
import { Card } from '../ui/Card';
import StatusPill from '../ui/StatusPill';
import { ChevronDownIcon, ChevronRightIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';

/** Group resources under their type; types with no resources still show. */
export function groupResourcesByType(types, resources) {
  const list = Array.isArray(types) ? types : [];
  const byType = new Map(list.map((t) => [String(t.id), []]));
  const orphans = [];
  (Array.isArray(resources) ? resources : []).forEach((r) => {
    const key = String(r.resourceTypeId ?? '');
    if (byType.has(key)) byType.get(key).push(r);
    else orphans.push(r);
  });
  const groups = list.map((t) => ({ type: t, resources: byType.get(String(t.id)) }));
  if (orphans.length) groups.push({ type: { id: '__other__', name: 'Other', active: true }, resources: orphans });
  return groups;
}

export default function MigratedResourcesView({ types, resources }) {
  const groups = useMemo(() => groupResourcesByType(types, resources), [types, resources]);
  const [collapsed, setCollapsed] = useState([]);
  const toggle = (id) => setCollapsed((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="space-y-3" data-testid="migrated-resources">
      {groups.map(({ type, resources: rows }) => {
        const id = String(type.id);
        const open = !collapsed.includes(id);
        return (
          <Card key={id} className="overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(id)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left"
              aria-expanded={open}
            >
              {open
                ? <ChevronDownIcon className="h-4 w-4" style={{ color: 'var(--color-text-subtle)' }} />
                : <ChevronRightIcon className="h-4 w-4" style={{ color: 'var(--color-text-subtle)' }} />}
              <RectangleGroupIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
              <span className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>{type.name}</span>
              <span className="text-xs" style={{ color: 'var(--color-text-subtle)' }}>({rows.length})</span>
              {type.active === false && <StatusPill status="inactive" size="sm" />}
            </button>
            {open && (
              <div style={{ borderTop: '1px solid var(--color-border)' }}>
                {rows.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm" style={{ color: 'var(--color-text-subtle)' }}>
                    No {type.name} resources.
                  </p>
                ) : (
                  <ul>
                    {rows.map((r) => (
                      <li key={r.id} className="flex items-center gap-2 px-4 py-2.5" style={{ borderTop: '1px solid var(--color-border)' }}>
                        <span className="text-sm truncate" style={{ color: 'var(--color-text-default)' }}>{r.name}</span>
                        {r.description && (
                          <span className="text-xs truncate" style={{ color: 'var(--color-text-subtle)' }} title={r.description}>
                            {r.description}
                          </span>
                        )}
                        {r.active === false && <StatusPill status="inactive" size="sm" />}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
